import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import TypeAhead from "@/components/TypeAhead";
import { Plus, Trash2, PackagePlus, WifiOff, Wifi, CloudUpload, Truck, X, ClipboardList } from "lucide-react";
import { useOfflineReceiptQueue } from "@/lib/offlineQueue";
import { useLabels } from "@/lib/auth";

const today = () => new Date().toISOString().slice(0, 10);

let entryKey = 0;
const newEntry = (defaults = {}) => ({
  key: ++entryKey,
  item: null,
  locationId: "",
  declaredQty: "",
  supplierLot: "",
  unitCost: "",
  lines: [{ count: 1, qty_each: "", description: "" }],
  booking: null,
  ...defaults,
});

export default function GoodsReceipt({ receiverMode = false }) {
  const qc = useQueryClient();
  const L = useLabels();
  const [supplier, setSupplier] = useState(null);
  const [receivedDate, setReceivedDate] = useState(today());
  const [notes, setNotes] = useState("");
  const [entries, setEntries] = useState([newEntry()]);
  const [busy, setBusy] = useState(false);
  const [locations, setLocations] = useState([]);
  const { queue, online, submitOrQueue, flush, removeItem } = useOfflineReceiptQueue();

  const { data: expected = [] } = useQuery({
    queryKey: ["bookings-open"],
    queryFn: async () => (await api.get("/bookings", { params: { status: "booked" } })).data,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data.filter(l => l.active))).catch(() => {});
  }, []);

  const patchEntry = (key, patch) =>
    setEntries((E) => E.map((e) => (e.key === key ? { ...e, ...patch } : e)));
  const removeEntry = (key) =>
    setEntries((E) => (E.length > 1 ? E.filter((e) => e.key !== key) : E));
  const addEntry = () =>
    setEntries((E) => [...E, newEntry({ locationId: E[E.length - 1]?.locationId || "" })]);

  const startFromBooking = (b) => {
    if (!supplier) setSupplier({ id: b.supplier_id, name: b.supplier_name, code: b.supplier_code });
    const filled = {
      item: { id: b.item_id, name: b.item_name, sku: b.item_sku },
      declaredQty: String(b.ordered_qty),
      supplierLot: "",
      booking: b,
    };
    setEntries((E) => {
      const blank = E.find((e) => !e.item && !e.declaredQty);
      if (blank) return E.map((e) => (e.key === blank.key ? { ...e, ...filled } : e));
      return [...E, newEntry({ ...filled, locationId: E[E.length - 1]?.locationId || "" })];
    });
    if (b.lr_number || b.transporter) {
      setNotes((n) => n || [b.lr_number && `LR ${b.lr_number}`, b.transporter].filter(Boolean).join(" · "));
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const entryStats = (e) => {
    const sum = e.lines.reduce((s, l) => s + (Number(l.count) || 0) * (Number(l.qty_each) || 0), 0);
    const declared = Number(e.declaredQty) || 0;
    const linesOk = e.lines.every((l) => l.count && l.qty_each && l.description);
    const matches = Math.abs(sum - declared) < 0.0001 && declared > 0 && linesOk;
    const ready = matches && !!e.item && !!e.locationId;
    return { sum, declared, matches, ready };
  };

  const allReady = supplier && entries.length > 0 && entries.every((e) => entryStats(e).ready);
  const totalContainers = entries.reduce(
    (s, e) => s + e.lines.reduce((c, l) => c + (Number(l.count) || 0), 0), 0);

  const submit = async () => {
    if (!allReady) return;
    setBusy(true);
    const failed = [];
    let posted = 0;
    for (const e of entries) {
      const payload = {
        item_id: e.item.id,
        supplier_id: supplier.id,
        location_id: e.locationId,
        received_date: receivedDate,
        supplier_lot: e.supplierLot || null,
        unit_cost: e.unitCost ? Number(e.unitCost) : null,
        notes: notes || null,
        declared_qty: Number(e.declaredQty),
        booking_id: e.booking?.id || null,
        container_lines: e.lines.map((l) => ({
          count: Number(l.count),
          qty_each: Number(l.qty_each),
          description: l.description,
        })),
      };
      try {
        const res = await submitOrQueue(payload);
        posted++;
        if (res.queued) {
          toast.info(`${e.item.name}: queued — will sync when online`);
        } else {
          toast.success(
            receiverMode
              ? `${e.item.name}: submitted for approval`
              : `${e.item.name} · ${res.data.batch_number}`,
          );
        }
      } catch (err) {
        failed.push(e);
        toast.error(`${e.item.name}: ${formatError(err)}`);
      }
    }
    if (failed.length) {
      setEntries(failed); // keep only the ones that need fixing
    } else if (posted > 0) {
      setSupplier(null); setNotes("");
      setEntries([newEntry()]);
    }
    qc.invalidateQueries({ queryKey: ["bookings-open"] });
    setBusy(false);
  };

  return (
    <div className="space-y-6 max-w-4xl animate-fade-in" data-testid="grn-form">
      {!receiverMode && (
        <div>
          <div className="data-label">Inward</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Receive Stock</h1>
          <p className="text-sm text-muted-foreground mt-1">
            One truck, one entry — add every item on it below. Booked something? Pick it from
            Expected deliveries and it pre-fills. Unannounced delivery? Just fill the form.
          </p>
        </div>
      )}

      {/* Connection + queue indicator */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="grn-connection">
        <span className={`badge-flat ${online ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
          {online ? <><Wifi className="w-3 h-3 mr-1 inline" /> online</> : <><WifiOff className="w-3 h-3 mr-1 inline" /> offline</>}
        </span>
        {queue.length > 0 && (
          <>
            <span className="badge-flat bg-secondary/15 text-secondary" data-testid="grn-queue-count">
              <CloudUpload className="w-3 h-3 mr-1 inline" /> {queue.length} queued
            </span>
            <button onClick={flush} className="text-xs uppercase tracking-widest font-semibold text-primary"
                    data-testid="grn-flush-btn">Sync now</button>
          </>
        )}
      </div>

      {queue.length > 0 && (
        <div className="bg-white border border-secondary/30 rounded-sm p-3 space-y-2" data-testid="grn-queue-list">
          <div className="data-label">Queued receipts</div>
          <ul className="text-xs divide-y divide-border">
            {queue.map(q => (
              <li key={q.id} className="py-2 flex items-center gap-2">
                <span className="font-mono">{q.payload.declared_qty}</span>
                <span className="text-muted-foreground truncate flex-1">
                  {new Date(q.queued_at).toLocaleTimeString()} · attempts: {q.attempts}
                  {q.last_error && <span className="text-destructive ml-2">· {q.last_error}</span>}
                </span>
                <button onClick={() => removeItem(q.id)}
                        className="text-destructive hover:underline"
                        data-testid={`grn-queue-rm-${q.id}`}>drop</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Expected deliveries — optional shortcut, not a requirement */}
      {expected.length > 0 && (
        <div className="bg-white border border-secondary/50 rounded-sm" data-testid="expected-deliveries">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <Truck className="w-4 h-4 text-secondary" />
            <h2 className="font-display text-lg font-semibold">Expected deliveries · {expected.length}</h2>
          </div>
          <ul className="divide-y divide-border">
            {expected.map(b => (
              <li key={b.id} className="p-3 flex items-center gap-3 flex-wrap text-sm"
                  data-testid={`booking-${b.number}`}>
                <span className="container-code text-primary">{b.number}</span>
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{b.item_name}</span>
                  <span className="text-muted-foreground"> · {b.ordered_qty} {b.base_unit} · {b.supplier_name}</span>
                  <div className="text-xs text-muted-foreground">
                    {b.lr_number && <>LR {b.lr_number} · </>}
                    {b.transporter && <>{b.transporter} · </>}
                    {b.expected_date && <>expected {b.expected_date}</>}
                  </div>
                </div>
                <button className="btn-primary text-xs" onClick={() => startFromBooking(b)}
                        data-testid={`booking-receive-${b.number}`}>
                  <PackagePlus className="w-4 h-4 mr-1" /> Receive this
                </button>
                {!receiverMode && (
                  <button className="btn-ghost text-xs text-destructive"
                          onClick={async () => {
                            if (!window.confirm(`Cancel booking ${b.number}?`)) return;
                            try {
                              await api.patch(`/bookings/${b.id}`, { status: "cancelled" });
                              qc.invalidateQueries({ queryKey: ["bookings-open"] });
                            } catch (e) { toast.error(formatError(e)); }
                          }}>
                    Cancel
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!receiverMode && <BookOrderForm onBooked={() => qc.invalidateQueries({ queryKey: ["bookings-open"] })} />}

      {/* Shared truck-level details */}
      <div className="bg-white border border-border rounded-sm p-4 sm:p-6 space-y-4">
        <div className="data-label">This delivery (shared by all items below)</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label={L.Source}>
            <TypeAhead
              endpoint="/suppliers/search"
              value={supplier}
              onChange={setSupplier}
              subLabelKey="code"
              placeholder={`Search ${L.source} (≥3 chars)`}
              testid="grn-supplier"
            />
          </Field>
          <Field label="Received date">
            <input
              data-testid="grn-date"
              type="date"
              className="field"
              value={receivedDate}
              onChange={(e) => setReceivedDate(e.target.value)}
            />
          </Field>
          <Field label="Notes / LR no. (optional)">
            <input
              data-testid="grn-notes"
              className="field"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. LR 4521 · VRL Logistics"
            />
          </Field>
        </div>
      </div>

      {/* One card per item on the truck */}
      {entries.map((e, idx) => (
        <ItemCard
          key={e.key}
          idx={idx}
          entry={e}
          stats={entryStats(e)}
          locations={locations}
          receiverMode={receiverMode}
          onPatch={(patch) => patchEntry(e.key, patch)}
          onRemove={() => removeEntry(e.key)}
          removable={entries.length > 1}
        />
      ))}

      <button onClick={addEntry} className="btn-ghost text-sm" data-testid="grn-add-item">
        <Plus className="w-4 h-4 mr-1" /> Add another item from this delivery
      </button>

      {/* Sticky summary */}
      <div className={`sticky bottom-0 z-10 -mx-4 sm:mx-0 px-4 sm:px-5 py-3 border-t sm:border sm:rounded-sm ${
        allReady ? "bg-success/10 border-success/30" : "bg-white border-border"}`}
        data-testid="grn-running-total"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="text-sm">
            <div className="data-label">Ready to post</div>
            <div className="font-mono text-xl font-bold">
              {entries.filter((e) => entryStats(e).ready).length}/{entries.length}
              <span className="text-muted-foreground text-sm"> items · {totalContainers} containers</span>
            </div>
          </div>
          <button
            data-testid="grn-submit"
            onClick={submit}
            disabled={!allReady || busy}
            className="btn-primary"
          >
            <PackagePlus className="w-4 h-4 mr-2" />
            {busy ? "Submitting…"
              : receiverMode ? "Submit for approval"
              : entries.length > 1 ? `Post ${entries.length} receipts` : "Post receipt"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ItemCard({ idx, entry, stats, locations, receiverMode, onPatch, onRemove, removable }) {
  const updateLine = (i, patch) =>
    onPatch({ lines: entry.lines.map((l, ix) => (ix === i ? { ...l, ...patch } : l)) });
  const addLine = () => onPatch({ lines: [...entry.lines, { count: 1, qty_each: "", description: "" }] });
  const rmLine = (i) => onPatch({ lines: entry.lines.filter((_, ix) => ix !== i) });
  const shortage = stats.declared - stats.sum;

  return (
    <div className={`bg-white border rounded-sm p-4 sm:p-6 space-y-4 ${
      stats.ready ? "border-success/40" : "border-border"}`}
      data-testid={`grn-item-card-${idx}`}
    >
      <div className="flex items-center justify-between">
        <span className="data-label">Item {idx + 1}</span>
        <div className="flex items-center gap-2">
          {entry.booking && (
            <span className="badge-flat bg-secondary/15 text-secondary" data-testid={`grn-item-${idx}-booking`}>
              <Truck className="w-3 h-3 mr-1 inline" /> {entry.booking.number}
            </span>
          )}
          {removable && (
            <button onClick={onRemove} className="text-muted-foreground hover:text-destructive"
                    data-testid={`grn-item-${idx}-rm`}>
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Item">
          <TypeAhead
            endpoint="/items/search"
            value={entry.item}
            onChange={(v) => onPatch({ item: v })}
            subLabelKey="sku"
            placeholder="Search item (≥3 chars)"
            testid={`grn-item-${idx}`}
          />
        </Field>
        <Field label="Where did you keep it?">
          <select
            data-testid={`grn-location-${idx}`}
            className="field"
            value={entry.locationId}
            onChange={(e) => onPatch({ locationId: e.target.value })}
          >
            <option value="">Select location</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.code} · {l.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Lot no. (optional)">
          <input
            data-testid={`grn-lot-${idx}`}
            className="field"
            value={entry.supplierLot}
            onChange={(e) => onPatch({ supplierLot: e.target.value })}
          />
        </Field>
        {!receiverMode && (
          <Field label="Unit cost (optional)">
            <input
              data-testid={`grn-cost-${idx}`}
              type="number"
              step="0.01"
              className="field"
              value={entry.unitCost}
              onChange={(e) => onPatch({ unitCost: e.target.value })}
            />
          </Field>
        )}
      </div>

      <Field label="Declared total quantity (base unit)">
        <input
          data-testid={`grn-declared-${idx}`}
          type="number"
          step="0.001"
          className="field font-mono text-lg font-semibold"
          value={entry.declaredQty}
          onChange={(e) => onPatch({ declaredQty: e.target.value })}
          placeholder="e.g. 90"
        />
      </Field>

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="data-label">Container breakdown</span>
          <button type="button" onClick={addLine} data-testid={`grn-add-line-${idx}`}
                  className="text-primary text-xs font-semibold uppercase tracking-widest flex items-center gap-1">
            <Plus className="w-4 h-4" /> Add row
          </button>
        </div>
        <div className="space-y-2">
          {entry.lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`grn-item-${idx}-line-${i}`}>
              <input
                className="field-sm col-span-2 text-center font-mono"
                placeholder="Qty"
                type="number" min="1"
                value={l.count}
                onChange={(e) => updateLine(i, { count: e.target.value })}
              />
              <span className="text-muted-foreground text-center">×</span>
              <input
                className="field-sm col-span-3 font-mono"
                placeholder="Qty each"
                type="number" step="0.001"
                value={l.qty_each}
                onChange={(e) => updateLine(i, { qty_each: e.target.value })}
              />
              <input
                className="field-sm col-span-5"
                placeholder="Container description"
                value={l.description}
                onChange={(e) => updateLine(i, { description: e.target.value })}
              />
              <button
                type="button"
                onClick={() => rmLine(i)}
                className="col-span-1 text-muted-foreground hover:text-destructive"
                disabled={entry.lines.length === 1}
              >
                <Trash2 className="w-4 h-4 mx-auto" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="text-sm font-mono">
        {stats.sum.toFixed(3)} <span className="text-muted-foreground">/ {stats.declared.toFixed(3)}</span>
        {!stats.matches && stats.declared > 0 && (
          <span className="text-xs text-muted-foreground ml-2">
            {shortage > 0 ? `short by ${shortage.toFixed(3)}` : `over by ${(-shortage).toFixed(3)}`}
          </span>
        )}
        {stats.matches && <span className="text-success text-xs ml-2">✓ matches</span>}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="data-label">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

/** Log an order the moment you place it — LR/transporter can be added later.
 * Entirely optional: unannounced deliveries go straight into the form above. */
function BookOrderForm({ onBooked }) {
  const L = useLabels();
  const [open, setOpen] = useState(false);
  const [item, setItem] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [qty, setQty] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  const [lr, setLr] = useState("");
  const [transporter, setTransporter] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", {
        item_id: item.id, supplier_id: supplier.id, ordered_qty: Number(qty),
        expected_date: expectedDate || null, lr_number: lr || null,
        transporter: transporter || null, notes: notes || null,
      });
      toast.success(`Booked · ${data.number}`);
      setItem(null); setSupplier(null); setQty(""); setExpectedDate("");
      setLr(""); setTransporter(""); setNotes(""); setOpen(false);
      onBooked();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button className="btn-ghost text-sm" onClick={() => setOpen(true)} data-testid="book-order-btn">
        <ClipboardList className="w-4 h-4 mr-1" /> + Book an order (so the godown knows it's coming)
      </button>
    );
  }
  return (
    <div className="bg-white border border-border rounded-sm p-4 space-y-3" data-testid="book-order-form">
      <div className="flex items-center justify-between">
        <span className="data-label">Book an order</span>
        <button onClick={() => setOpen(false)} className="text-muted-foreground"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <TypeAhead endpoint="/items/search" value={item} onChange={setItem}
                   subLabelKey="sku" placeholder="Item (≥3 chars)" testid="book-item" />
        <TypeAhead endpoint="/suppliers/search" value={supplier} onChange={setSupplier}
                   subLabelKey="code" placeholder={`${L.Source} (≥3 chars)`} testid="book-supplier" />
        <input className="field font-mono" type="number" step="0.001" placeholder="Ordered qty"
               value={qty} onChange={(e) => setQty(e.target.value)} data-testid="book-qty" />
        <input className="field" type="date" value={expectedDate}
               onChange={(e) => setExpectedDate(e.target.value)} data-testid="book-date" />
        <input className="field" placeholder="LR / docket no. (can add later)"
               value={lr} onChange={(e) => setLr(e.target.value)} data-testid="book-lr" />
        <input className="field" placeholder="Transporter (optional)"
               value={transporter} onChange={(e) => setTransporter(e.target.value)} data-testid="book-transporter" />
      </div>
      <input className="field" placeholder="Notes (optional)"
             value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="book-notes" />
      <div className="flex justify-end">
        <button className="btn-primary" disabled={busy || !item || !supplier || !Number(qty)}
                onClick={submit} data-testid="book-submit">
          {busy ? "Saving…" : "Book order"}
        </button>
      </div>
    </div>
  );
}
